import { useTranslation } from "react-i18next";
import Card from "../../components/Card/Card";
import { CardContent, CardHeader, CardTitle, CardV2 } from "../../components/CardV2/CardV2";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import Subtitle from "../../components/Subtitle/Subtitle";
import PageWrapper from "../PageWrapper/PageWrapper";
import { useState, Fragment } from "react";
import Pill from "../../components/Pill/Pill";
import { Modal } from "../../components/Modal/Modal";
import FAQ from "../../components/FAQ/FAQ";

type Currency = 'EUR' | 'USD' | 'PLN';

type Pricing = {
  [K in Currency]: number;
};

type Offer = {
  id: string;
  title: string;
  pricing: Pricing;
  period?: string; // "month", "one-time", etc.
  description: string;
  features: string[];
  imageUrl: string;
  paymentLink?: string;
};

const offers: Offer[] = [
  {
    id: "basic-maintenance",
    title: "benefits_page.offers.basic_maintenance.title",
    pricing: {
      EUR: 29.99,
      USD: 29.99,
      PLN: 99.99
    },
    period: 'month',
    description: "benefits_page.offers.basic_maintenance.description",
    features: [
      "benefits_page.offers.basic_maintenance.features.feature_1",
      "benefits_page.offers.basic_maintenance.features.feature_2",
      "benefits_page.offers.basic_maintenance.features.feature_3",
      "benefits_page.offers.basic_maintenance.features.feature_4",
      "benefits_page.offers.basic_maintenance.features.feature_5",
      "benefits_page.offers.basic_maintenance.features.feature_6"
    ],
    imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    paymentLink: "https://buy.stripe.com/7sY4gzcmw8tNcQQ3at3F60g"
  },
  {
    id: "basic-website",
    title: "benefits_page.offers.basic_website.title",
    pricing: {
      EUR: 299.99,
      USD: 299.99,
      PLN: 999.99
    },
    period: 'one-time',
    description: "benefits_page.offers.basic_website.description",
    features: [
      "benefits_page.offers.basic_website.features.feature_1",
      "benefits_page.offers.basic_website.features.feature_2",
      "benefits_page.offers.basic_website.features.feature_3",
      "benefits_page.offers.basic_website.features.feature_4",
      "benefits_page.offers.basic_website.features.feature_5",
      "benefits_page.offers.basic_website.features.feature_6",
      "benefits_page.offers.basic_website.features.feature_7",
      "benefits_page.offers.basic_website.features.feature_8"
    ],
    imageUrl: "https://images.unsplash.com/photo-1547658719-da2b51169166?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    paymentLink: "https://buy.stripe.com/8x24gz9akeSb4kk5iB3F60h"
  },
  {
    id: "advanced-website",
    title: "benefits_page.offers.advanced_website.title",
    pricing: {
      EUR: 899.99,
      USD: 899.99,
      PLN: 3499.99
    },
    period: 'one-time',
    description: "benefits_page.offers.advanced_website.description",
    features: [
      "benefits_page.offers.advanced_website.features.feature_1",
      "benefits_page.offers.advanced_website.features.feature_2",
      "benefits_page.offers.advanced_website.features.feature_3",
      "benefits_page.offers.advanced_website.features.feature_4",
      "benefits_page.offers.advanced_website.features.feature_5",
      "benefits_page.offers.advanced_website.features.feature_6",
      "benefits_page.offers.advanced_website.features.feature_7",
      "benefits_page.offers.advanced_website.features.feature_8",
      "benefits_page.offers.advanced_website.features.feature_9",
      "benefits_page.offers.advanced_website.features.feature_10"
    ],
    imageUrl: "https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    paymentLink: "https://buy.stripe.com/cNi8wPgCMh0j2cccL33F60i"
  },
];

const currencySymbols: Record<Currency, string> = {
  'EUR': '€',
  'USD': '$',
  'PLN': 'PLN'
};

const formatPrice = (price: number, currency: Currency, period?: string): string => {
  const symbol = currencySymbols[currency];
  const formattedPrice = price.toFixed(2);

  return `${formattedPrice} ${symbol}`;
};

// Service Information Modal Component
const ServiceInfoModal = ({ isOpen, onClose, selectedOffer }: { 
  isOpen: boolean; 
  onClose: () => void; 
  selectedOffer: Offer | null; 
}) => {
  const { t, i18n } = useTranslation();
  
  // Determine currency based on language
  const getCurrencyFromLanguage = (): Currency => {
    const language = i18n.language;
    if (language.startsWith('pl')) return 'PLN';
    if (language.startsWith('de') || language.startsWith('es') || language.startsWith('it') || language.startsWith('pt') || language.startsWith('uk')) return 'EUR';
    return 'USD';
  };
  
  const defaultCurrency = getCurrencyFromLanguage();

  if (!isOpen || !selectedOffer) return null;

  const handlePurchase = () => {
    if (selectedOffer.paymentLink) {
      window.open(selectedOffer.paymentLink, '_blank');
    }
    onClose();
  };

  return (
    <Modal
      modalTitle={t(selectedOffer.title)}
      canClose={true}
      onClose={onClose}
    >
      <div className="space-y-6">
        {/* Price */}
        <div>
          <div className="flex items-center gap-3">
            <p className="text-3xl font-bold text-gold-600">
              {formatPrice(selectedOffer.pricing[defaultCurrency], defaultCurrency, selectedOffer.period)}
            </p>
            <Pill 
              text={t(selectedOffer.period === 'month' ? 'benefits_page.pill.subscription' : 'benefits_page.pill.one_time')} 
              color={selectedOffer.period === 'month' ? 'blue' : 'green'}
            />
          </div>
        </div>

        {/* Service Process */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('benefits_page.modal.how_it_works')}</h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-gold-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>{t('benefits_page.modal.step_1.title')}</strong> - {t('benefits_page.modal.step_1.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-gold-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>{t('benefits_page.modal.step_2.title')}</strong> - {t('benefits_page.modal.step_2.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-gold-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>{t('benefits_page.modal.step_3.title')}</strong> - {t('benefits_page.modal.step_3.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-gold-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>{t('benefits_page.modal.step_4.title')}</strong> - {t('benefits_page.modal.step_4.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What's included */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('benefits_page.modal.whats_included')}</h3>
          <ul className="space-y-2">
            {selectedOffer.features.map((feature, index) => (
              <li key={index} className="flex items-center text-sm text-gray-700">
                <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t(feature)}
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center pt-4">
          <PrimaryButton
            text={t('benefits_page.modal.purchase_now')!}
            fn={handlePurchase}
            actionName={`Purchase ${t(selectedOffer.title)}`}
          />
        </div>
      </div>
    </Modal>
  );
};

function BenefitsPage() {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  
  // Determine currency based on language
  const getCurrencyFromLanguage = (): Currency => {
    const language = i18n.language;
    if (language.startsWith('pl')) return 'PLN';
    if (language.startsWith('de') || language.startsWith('es') || language.startsWith('it') || language.startsWith('pt') || language.startsWith('uk')) return 'EUR';
    return 'USD';
  };
  
  const defaultCurrency = getCurrencyFromLanguage();

  const handleGetOffer = (offer: Offer) => {
    setSelectedOffer(offer);
    setIsModalOpen(true);
  };

  return (
    <PageWrapper>
      <Fragment>
          <div className="mt-5 space-y-5">
          {/* Introduction Section */}
          <div>
            <Card>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
                <div className="text-center space-y-4">
                  <Subtitle text={String(t('benefits_page.introduction.title'))} />
                  <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
                    {t('benefits_page.introduction.description')}
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('benefits_page.introduction.benefit_1')}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('benefits_page.introduction.benefit_2')}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('benefits_page.introduction.benefit_3')}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <Card key={offer.id}>
                <div className="flex flex-col h-full">
                  {/* Header Image with Gradient Overlay */}
                  <div className="relative h-48 rounded-lg overflow-hidden mb-4">
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center z-0"
                      style={{ backgroundImage: `url(${offer.imageUrl})` }}
                    />
                    
                    {/* Gradient Overlay - Multiple approaches */}
                    <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-24 z-20 bg-gradient-to-t from-black to-transparent"></div>
                    
                    {/* Content Overlay */}
                    <div className="absolute inset-0 flex flex-col justify-end p-4 z-30">
                      <h3 className="text-xl font-bold text-white mb-1">{t(offer.title)}</h3>
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold text-gold-300">
                          {formatPrice(offer.pricing[defaultCurrency], defaultCurrency, offer.period)}
                        </p>
                        <Pill 
                          text={t(offer.period === 'month' ? 'benefits_page.pill.subscription' : 'benefits_page.pill.one_time')} 
                          color={offer.period === 'month' ? 'blue' : 'green'}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1">
                    <div className="flex-1 space-y-4">
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {t(offer.description)}
                      </p>

                      {/* Features List */}
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{t('benefits_page.card.whats_included')}</h4>
                        <ul className="space-y-1">
                          {offer.features.map((feature, index) => (
                            <li key={index} className="flex items-center text-sm text-gray-600">
                              <svg 
                                className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                              >
                                <path 
                                  fillRule="evenodd" 
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                  clipRule="evenodd" 
                                />
                              </svg>
                              {t(feature)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Action Button - Aligned to bottom */}
                    <div className="pt-4 mt-auto flex justify-center">
                      <PrimaryButton
                        text={t('benefits_page.card.get_started')!}
                        fn={() => handleGetOffer(offer)}
                        actionName={`Get ${t(offer.title)}`}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* FAQ Section */}
          <div>
            <CardV2>
              <CardHeader>
                <CardTitle className="text-center">{t('global.faq.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <FAQ 
                  items={[
                    {
                      id: 'existing-website',
                      questionKey: 'benefits_page.faq.existing_website.question',
                      answerKey: 'benefits_page.faq.existing_website.answer'
                    },
                    {
                      id: 'no-idea',
                      questionKey: 'benefits_page.faq.no_idea.question',
                      answerKey: 'benefits_page.faq.no_idea.answer'
                    },
                    {
                      id: 'maintenance',
                      questionKey: 'benefits_page.faq.maintenance.question',
                      answerKey: 'benefits_page.faq.maintenance.answer'
                    },
                    {
                      id: 'learn-maintenance',
                      questionKey: 'benefits_page.faq.learn_maintenance.question',
                      answerKey: 'benefits_page.faq.learn_maintenance.answer'
                    },
                    {
                      id: 'timeline',
                      questionKey: 'benefits_page.faq.timeline.question',
                      answerKey: 'benefits_page.faq.timeline.answer'
                    },
                    {
                      id: 'revisions',
                      questionKey: 'benefits_page.faq.revisions.question',
                      answerKey: 'benefits_page.faq.revisions.answer'
                    },
                    {
                      id: 'integration',
                      questionKey: 'benefits_page.faq.integration.question',
                      answerKey: 'benefits_page.faq.integration.answer'
                    }
                  ]}
                />
              </CardContent>
            </CardV2>
          </div>
        </div>

        {/* Service Information Modal */}
        {isModalOpen && selectedOffer && (
          <ServiceInfoModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            selectedOffer={selectedOffer}
          />
        )}
      </Fragment>
    </PageWrapper>
  );
}

export default BenefitsPage; 